import axios, { type InternalAxiosRequestConfig } from "axios";

import { getAuthOrganizationId } from "@/state/authContextBridge";
import { clearWorkspaceScopedClientState } from "@/state/workspaceClientState";
import { notifyNonCsrfForbidden } from "@/features/auth/accessDeniedSignal";
import { useWorkspaceStore } from "@/state/workspace.store";
import { normalizeDuplicateApiPath } from "@/lib/api/normalizeDuplicateApiPath";
import { createCrossTabRefresh, type CrossTabRefresh } from "@/lib/auth/crossTabRefresh";
import { announceSessionEnded } from "@/lib/auth/sessionEndBroadcast";
import { normalizeAxiosError } from "@/lib/api/normalizeError";
import { resolveProductionApiBaseUrl } from "@/lib/api/resolveApiBaseUrl";
import { invalidateStaleWorkspaceOnMismatch, isStaleWorkspaceRecoverySkipped } from "@/lib/workspaceOrgMismatch";

const baseURL = import.meta.env.PROD
  ? resolveProductionApiBaseUrl(import.meta.env.VITE_API_URL)
  : "/api";

export const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

type ZephixAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  __csrfRetried?: boolean;
};

/* ─── Access-token refresh (401) — single-flight + queue ───────── */

let isRefreshing = false;
const failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processRefreshQueue(error: unknown | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error != null) reject(error);
    else resolve();
  });
  failedQueue.length = 0;
}

export function isAuthRefreshBypassUrl(url: string): boolean {
  const u = String(url || "");
  return (
    u.includes("/auth/login") ||
    u.includes("/auth/register") ||
    u.includes("/auth/signup") ||
    u.includes("/auth/csrf") ||
    u.includes("/auth/refresh") ||
    u.includes("/auth/logout")
  );
}

/* ─── CSRF Token helpers ──────────────────────────────────────── */

/**
 * In-memory CSRF token store.
 * We store the token from the API response body instead of relying on cookies,
 * because cross-origin deployments (frontend/backend on different subdomains)
 * prevent the frontend from reading cookies set by the backend.
 */
let csrfTokenCache: string | null = null;

/**
 * Phase 14 (2026-04-09) — Public hook to invalidate the CSRF cache.
 *
 * Must be called by the auth flow on logout AND on login (to clear any
 * cache from a previous session). Without this, the module-level
 * `csrfTokenCache` survives across logins until the page reloads,
 * causing 403 / "CSRF token is required" errors on the new session's
 * first mutating request.
 *
 * The 403 response interceptor below also clears the cache + retries,
 * but that's a recovery path. Logout/login should clear proactively.
 */
export function clearCsrfTokenCache(): void {
  csrfTokenCache = null;
}

/**
 * Cross-site split hosts (e.g. separate `*.up.railway.app` for SPA vs API) may block
 * third-party cookies even with SameSite=None. Login/refresh responses already include
 * JWTs in JSON — keep them in memory and send `Authorization: Bearer` + refresh body
 * so `/auth/me` and the rest of the API work without relying on `zephix_session` cookies.
 */
let memoryAccessToken: string | null = null;
let memoryRefreshToken: string | null = null;

export function setMemoryAuthTokens(access: string | null, refresh: string | null): void {
  memoryAccessToken = access && access.length > 0 ? access : null;
  memoryRefreshToken = refresh && refresh.length > 0 ? refresh : null;
  // REFRESH-MULTITAB-1: a tab holding tokens must hear other tabs' renewals
  // from now on, not only after its own first refresh.
  if (memoryAccessToken || memoryRefreshToken) getCrossTabRefresh();
}

export function clearMemoryAuthTokens(): void {
  memoryAccessToken = null;
  memoryRefreshToken = null;
}

/**
 * When the API envelope includes `meta`, the axios success interceptor returns
 * `{ __zephixInner: T, __zephixMeta }` instead of T. Callers that expect the payload
 * object (e.g. login tokens, `/auth/me` user) must unwrap first.
 */
export function unwrapZephixClientPayload<T = unknown>(payload: unknown): T | null {
  if (payload == null) return null;
  if (typeof payload === "object" && "__zephixInner" in (payload as object)) {
    return (payload as { __zephixInner: T }).__zephixInner;
  }
  return payload as T;
}

function shouldAttachMemoryAccessBearer(url: string): boolean {
  const u = String(url || "");
  if (!memoryAccessToken) return false;
  return (
    !u.includes("/auth/login") &&
    !u.includes("/auth/register") &&
    !u.includes("/auth/signup") &&
    !u.includes("/auth/csrf") &&
    !u.includes("/auth/refresh") &&
    !u.includes("/auth/resend-verification") &&
    !u.includes("/auth/verify-email")
  );
}

function applyAccessTokenFromPayload(payload: unknown): void {
  const flat = unwrapZephixClientPayload<Record<string, unknown>>(payload);
  if (!flat || typeof flat !== "object") return;
  const at = flat.accessToken;
  if (typeof at === "string" && at.length > 0) {
    memoryAccessToken = at;
  }
  const rt = flat.refreshToken;
  if (typeof rt === "string" && rt.length > 0) {
    memoryRefreshToken = rt;
  }
}

/** In-memory access JWT for Authorization bearer (cross-site cookie fallback). */
export function getMemoryAccessToken(): string | null {
  return memoryAccessToken;
}

/** True when the renewal failed because the server answered 401 (not network / 5xx). */
function refusedByServer(err: unknown): boolean {
  const e = err as { status?: unknown; response?: { status?: unknown } } | null;
  return e?.status === 401 || e?.response?.status === 401;
}

/** POST /auth/refresh with this tab's current tokens (body token = cookie-blocked fallback). */
async function renewThisTab(): Promise<void> {
  clearCsrfTokenCache();
  const refreshPayload =
    memoryRefreshToken && typeof memoryRefreshToken === "string"
      ? { refreshToken: memoryRefreshToken }
      : {};
  const refreshed = await api.post("/auth/refresh", refreshPayload);
  applyAccessTokenFromPayload(refreshed);
}

let crossTabRefresh: CrossTabRefresh | null = null;
function getCrossTabRefresh(): CrossTabRefresh {
  if (!crossTabRefresh) {
    crossTabRefresh = createCrossTabRefresh({
      getTokens: () => ({ access: memoryAccessToken, refresh: memoryRefreshToken }),
      setTokens: (t) => setMemoryAuthTokens(t.access, t.refresh),
      renew: renewThisTab,
      // A GET /auth/me 401 resolves null in the interceptor (no nested refresh).
      sessionIsValid: async () => (await api.get("/auth/me")) != null,
      // Another tab signed in as someone else: this tab's tokens are already
      // cleared; reload so the boot check decides who is signed in here.
      onAccountSwitch: () => {
        clearMemoryAuthTokens();
        if (typeof window !== "undefined") window.location.reload();
      },
    });
  }
  return crossTabRefresh;
}

/**
 * Rotate the session via POST /auth/refresh (single-flight).
 * Used by both `api` and `apiClient` so a 401 never hard-redirects
 * before refresh is attempted (APICLIENT-401-NO-REFRESH-1).
 */
export async function refreshSession(): Promise<void> {
  if (isRefreshing) {
    await new Promise<void>((resolve, reject) => {
      failedQueue.push({
        resolve: () => resolve(),
        reject,
      });
    });
    return;
  }

  isRefreshing = true;
  // SIGNOUT-BROADCAST-1: only a tab that HAD a session announces its end, so an
  // anonymous cold boot (refresh 401 on /login) never signs other tabs out.
  const hadSession = Boolean(memoryAccessToken || memoryRefreshToken || getAuthOrganizationId());
  try {
    // REFRESH-MULTITAB-1: one renewal across tabs; other tabs adopt its tokens.
    await getCrossTabRefresh().renew();
    processRefreshQueue(null);
  } catch (refreshErr) {
    processRefreshQueue(refreshErr);
    // The server refused the renewal (expired or revoked): the session is over
    // in every tab. A network failure or 5xx is not an end, so it isn't announced.
    if (hadSession && refusedByServer(refreshErr)) announceSessionEnded("expired");
    throw refreshErr;
  } finally {
    isRefreshing = false;
  }
}

/** Read the XSRF-TOKEN cookie (works for same-origin only, fallback) */
function getCsrfCookie(): string | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith("XSRF-TOKEN="));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

/** Fetch a fresh CSRF token from the backend */
let csrfFetchPromise: Promise<string | null> | null = null;
async function ensureCsrfToken(): Promise<string | null> {
  // Try in-memory cache first, then cookie fallback
  if (csrfTokenCache) return csrfTokenCache;
  const cookie = getCsrfCookie();
  if (cookie) { csrfTokenCache = cookie; return cookie; }

  // Deduplicate concurrent calls
  if (csrfFetchPromise) return csrfFetchPromise;

  csrfFetchPromise = (async () => {
    try {
      const res = await axios.get(`${baseURL}/auth/csrf`, { withCredentials: true });
      // Backend returns { token, csrfToken } in the response body — use that
      // instead of reading cookies (which fails cross-origin)
      const token = res.data?.csrfToken || res.data?.token || getCsrfCookie();
      if (token) csrfTokenCache = token;
      return token || null;
    } catch {
      return null;
    } finally {
      csrfFetchPromise = null;
    }
  })();

  return csrfFetchPromise;
}

const MUTATING_METHODS = ["post", "put", "patch", "delete"];

/* ─── URL classification ─────────────────────────────────────── */

function isAuthUrl(url: string) {
  return url.includes("/auth/");
}

function isAuthMeUrl(url: string) {
  return url.endsWith("/auth/me") || url.includes("/auth/me?");
}

function isHealthUrl(url: string) {
  return url.includes("/health") || url.includes("/version");
}

function isWorkUrl(url: string) {
  return url.startsWith("/work/") || url.includes("/work/");
}

function isProjectsUrl(url: string) {
  return url.startsWith("/projects/") || url.includes("/projects/");
}

/** Organization admin API — must not send x-workspace-id (tenant interceptor validates it and can 403). */
export function isOrgAdminApiPath(url: string): boolean {
  const pathOnly = String(url || "").split("?")[0].replace(/^\/+/, "");
  return (
    pathOnly.startsWith("admin/") ||
    pathOnly.startsWith("api/admin/") ||
    pathOnly.includes("/admin/")
  );
}

/**
 * Org invite management — GET/POST /orgs/:orgId/invites and
 * POST/DELETE /orgs/:orgId/invites/:id[/resend]. Org-scoped via the path;
 * a stale x-workspace-id can 403 the same way /admin does.
 * Does not match POST /invites/accept.
 */
function isOrgInviteManagementPath(url: string): boolean {
  const pathOnly = String(url || "").split("?")[0].replace(/^\/+/, "");
  return /(?:^|\/)orgs\/[^/]+\/invites(?:\/|$)/.test(pathOnly);
}

/** Personal account APIs — org-scoped via JWT, not active workspace header. */
function isUsersMeApiPath(url: string): boolean {
  const pathOnly = String(url || "").split("?")[0].replace(/^\/+/, "");
  return pathOnly.startsWith("users/me");
}

/**
 * Cross-workspace personal queues — org-scoped via JWT, workspace-agnostic.
 * My Work (`/work/my-tasks`) aggregates the caller's assigned tasks across ALL
 * accessible workspaces, so it must NOT require an active workspace and must
 * not be blocked by the WORKSPACE_REQUIRED fast-fail below. A member with zero
 * accessible workspaces (cold start) still gets a valid 200-empty response.
 */
function isWorkspaceOptionalApiPath(url: string): boolean {
  const pathOnly = String(url || "").split("?")[0].replace(/^\/+/, "");
  return pathOnly.startsWith("work/my-tasks") || pathOnly.includes("/work/my-tasks");
}

/** POST /workspaces (org-level create) must not send x-workspace-id — creation is not scoped to the current workspace. */
/**
 * D6-REVIEW-REACH-1: the ONE server-resolved route that carries no workspace.
 *
 * GET /work/gate-submissions/:submissionId/review resolves submission ->
 * project -> workspace on the SERVER from the persisted row, scoped by the
 * organization on the JWT. It exists so a named gate approver who holds no
 * workspace membership — and therefore never populates activeWorkspaceId —
 * can still reach the gate they were named on. Gating it on an active
 * workspace here would make it unreachable for exactly the population it was
 * built for, client-side, before the request ever leaves the browser.
 *
 * ⚠️ EXACT match, anchored at BOTH ends, with the id shape pinned. This is
 * deliberately NOT a prefix test: a prefix would silently unguard whatever
 * route lands under `work/gate-submissions/:id/` next — `/evidence`,
 * `/approve` — none of which resolve their own workspace. Every sibling
 * `/work/*` route must keep failing fast. There is a test that widens this to
 * a prefix and proves the sibling guard goes quiet, so the anchoring is load
 * bearing and not decoration.
 */
const SERVER_RESOLVED_GATE_REVIEW_PATH =
  /^work\/gate-submissions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/review$/i;

export function isServerResolvedGateReviewPath(url: string): boolean {
  const pathOnly = String(url || "").split("?")[0].replace(/^\/+/, "");
  return SERVER_RESOLVED_GATE_REVIEW_PATH.test(pathOnly);
}

function isPostWorkspaceRootCreate(url: string, method: string): boolean {
  if (method.toLowerCase() !== "post") return false;
  const pathOnly = String(url || "").split("?")[0];
  const trimmed = pathOnly.replace(/^\/+|\/+$/g, "");
  return trimmed === "workspaces";
}

/**
 * API-HEADER-ONE-PREDICATE-1: the ONE rule for x-workspace-id, used by both HTTP clients
 * (this file's interceptor and lib/api/client.ts). A route it names carries NO workspace
 * header — a caller-supplied one is dropped too; every other route keeps a caller's header,
 * else gets the active workspace.
 * - auth, health / version: no workspace context;
 * - org admin and org invite management: org-scoped; a stale header 403s them;
 * - users/me: personal, org-scoped via the JWT;
 * - work/my-tasks: cross-workspace (a zero-workspace member still gets 200-empty);
 * - the server-resolved gate review (D6-REVIEW-REACH-1): the server derives the workspace;
 * - POST /workspaces: org-level create, not scoped to the current workspace.
 */
export function isWorkspaceHeaderExemptPath(url: string, method: string): boolean {
  return (
    isAuthUrl(url) ||
    isHealthUrl(url) ||
    isOrgAdminApiPath(url) ||
    isOrgInviteManagementPath(url) ||
    isUsersMeApiPath(url) ||
    isWorkspaceOptionalApiPath(url) ||
    isServerResolvedGateReviewPath(url) ||
    isPostWorkspaceRootCreate(url, method)
  );
}

/** Same path exclusions as workspace header: auth, health/version, org-admin, users/me, POST /workspaces root. */
function shouldSkipOrganizationHeader(url: string, method: string): boolean {
  return (
    isAuthUrl(url) ||
    isHealthUrl(url) ||
    isOrgAdminApiPath(url) ||
    isOrgInviteManagementPath(url) ||
    isUsersMeApiPath(url) ||
    isPostWorkspaceRootCreate(url, method)
  );
}

function applyOrganizationHeader(cfg: InternalAxiosRequestConfig, url: string, method: string): void {
  if (!cfg.headers) cfg.headers = {} as any;
  if (shouldSkipOrganizationHeader(url, method)) {
    delete (cfg.headers as any)["x-organization-id"];
    return;
  }
  const orgId = getAuthOrganizationId();
  if (orgId) {
    (cfg.headers as any)["x-organization-id"] = orgId;
  } else {
    delete (cfg.headers as any)["x-organization-id"];
  }
}

/** Mirrors Stack 2 `ApiClient` IDs for observability (portable pattern). */
function generateStack1RequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function generateStack1CorrelationId(): string {
  return `corr_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function applyTelemetryHeaders(cfg: InternalAxiosRequestConfig): void {
  if (!cfg.headers) cfg.headers = {} as any;
  (cfg.headers as any)["x-request-id"] = generateStack1RequestId();
  (cfg.headers as any)["x-correlation-id"] = generateStack1CorrelationId();
}

/* ─── Request interceptor ────────────────────────────────────── */

api.interceptors.request.use(async (cfg) => {
  const resolvedBase = String(cfg.baseURL ?? api.defaults.baseURL ?? "");
  const normalized = normalizeDuplicateApiPath(cfg.url, resolvedBase);
  if (normalized !== undefined && normalized !== cfg.url) {
    cfg.url = normalized;
  }

  const url = String(cfg.url || "");

  if (!cfg.headers) cfg.headers = {} as any;

  applyTelemetryHeaders(cfg);

  // ── CSRF: attach token on every mutating request ──
  const method = (cfg.method || "get").toLowerCase();
  if (MUTATING_METHODS.includes(method) && !isAuthUrl(url) && !isHealthUrl(url)) {
    const token = await ensureCsrfToken();
    if (token) {
      (cfg.headers as any)["x-csrf-token"] = token;
    }
  }

  if (shouldAttachMemoryAccessBearer(url)) {
    (cfg.headers as any)["Authorization"] = `Bearer ${memoryAccessToken}`;
  }

  applyOrganizationHeader(cfg, url, method);

  // ── Workspace header ──
  // API-HEADER-ONE-PREDICATE-1: the one shared predicate decides; a skip route carries no
  // workspace header, a caller-supplied one included (lib/api/client.ts does the same).
  if (isWorkspaceHeaderExemptPath(url, method)) {
    delete (cfg.headers as any)["x-workspace-id"];
    return cfg;
  }

  // A caller that already named a workspace keeps it. The active workspace
  // can be a different one; #1317 returns 404 when this header is not the
  // project's workspace.
  const supplied = (cfg.headers as { get?: (name: string) => unknown }).get?.(
    'x-workspace-id',
  );
  if (typeof supplied === 'string' && supplied.length > 0) {
    return cfg;
  }

  const wsId = useWorkspaceStore.getState().activeWorkspaceId;

  // Fail fast for routes that require workspace context
  if ((isWorkUrl(url) || isProjectsUrl(url)) && !wsId) {
    const err: any = new Error("WORKSPACE_REQUIRED");
    err.code = "WORKSPACE_REQUIRED";
    err.meta = { url };
    throw err;
  }

  if (wsId) {
    (cfg.headers as any)["x-workspace-id"] = wsId;
  } else {
    delete (cfg.headers as any)["x-workspace-id"];
  }

  return cfg;
});

api.interceptors.response.use(
  (res) => {
    const data = res?.data;
    if (data && typeof data === "object" && "data" in data) {
      const inner = (data as any).data;
      const meta = (data as any).meta;
      if (meta != null && typeof meta === "object") {
        return { __zephixInner: inner, __zephixMeta: meta };
      }
      return inner;
    }
    return data;
  },
  async (err) => {
    const method = String(err?.config?.method || "").toLowerCase();
    const url = String(err?.config?.url || "");
    const status = err?.response?.status;
    const cfg = err?.config as ZephixAxiosRequestConfig | undefined;

    // Keep: GET /auth/me 401 → null without interceptor refresh.
    // Cold-boot restore lives in AuthContext (coldBootRestoreSession) so both
    // 200 {user:null} and 401→null are covered with an explicit one-shot refresh.
    // Removing this would not fix 200-null boot, and would risk hard-redirect
    // races on anonymous /auth/me probes (FIX-LOGOUT-ON-REFRESH-1).
    if (status === 401 && method === "get" && isAuthMeUrl(url)) {
      return null;
    }

    /* ── 401: rotate session via POST /auth/refresh (cookie body on server), then retry ── */
    if (status === 401 && cfg && !cfg._retry && !isAuthRefreshBypassUrl(url)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api.request(cfg);
          })
          .catch((e) => Promise.reject(e));
      }

      cfg._retry = true;

      try {
        await refreshSession();
        const retried = await api.request(cfg);
        return retried;
      } catch (refreshErr) {
        const normalized = normalizeAxiosError(refreshErr);
        // Session expired — clear workspace cache (same as AuthContext.logout).
        clearWorkspaceScopedClientState();
        if (typeof window !== "undefined") {
          window.location.assign("/login?reason=session_expired");
        }
        return Promise.reject(normalized);
      }
    }

    // Stale x-workspace-id: invalidate cache so the next request does not keep failing.
    // WS-RECOVERY-404-1: 404 too (Step B's 404 + WORKSPACE_NOT_FOUND); the helper decides.
    // Skipped for requests about a non-active workspace (TR-2026-10-20-14c).
    if ((status === 403 || status === 404) && !isStaleWorkspaceRecoverySkipped(err?.config)) {
      invalidateStaleWorkspaceOnMismatch(err);
    }

    /*
     * Phase 14 (2026-04-09) — Auto-recover from stale CSRF token.
     *
     * When the backend session-token rotates (e.g. JWT refresh), it
     * issues a new CSRF token tied to the new session. The in-memory
     * `csrfTokenCache` still holds the OLD token from the previous
     * session, so the next mutating request fails with 403 +
     * "CSRF token is required".
     *
     * Symptoms before this fix:
     *   - User clicks Create Project → 403 / "CSRF token is required"
     *   - Logging out and back in does NOT fix it because the
     *     csrfTokenCache module-level state survives across logins
     *     until the page reloads.
     *
     * Fix:
     *   - Detect 403 + CSRF marker (status code OR error body code)
     *   - Invalidate csrfTokenCache
     *   - Re-fetch a fresh token
     *   - Retry the original request ONCE
     *   - If retry also fails, surface the original error so we don't
     *     loop forever
     *
     * This is a per-request retry, not a global recovery — it only
     * fires for mutating requests that hit a CSRF 403, and only retries
     * once. The `__csrfRetried` flag on the config prevents infinite
     * loops if the backend keeps rejecting.
     */
    const isCsrfError =
      status === 403 &&
      (err?.response?.data?.code === "CSRF_TOKEN_MISSING" ||
        err?.response?.data?.code === "CSRF_TOKEN_INVALID" ||
        /csrf/i.test(String(err?.response?.data?.message || "")));

    const csrfCfg = err?.config as ZephixAxiosRequestConfig | undefined;
    if (isCsrfError && csrfCfg && !csrfCfg.__csrfRetried) {
      csrfCfg.__csrfRetried = true;
      // Invalidate the cache so ensureCsrfToken fetches fresh.
      csrfTokenCache = null;
      try {
        const fresh = await ensureCsrfToken();
        if (fresh) {
          // Set the new token on the retry config and re-issue.
          if (!csrfCfg.headers) csrfCfg.headers = {} as any;
          (csrfCfg.headers as any)["x-csrf-token"] = fresh;
          return api.request(csrfCfg);
        }
      } catch {
        // Fall through to throw original error.
      }
    }

    if (status === 403 && !isCsrfError) {
      notifyNonCsrfForbidden();
    }

    throw normalizeAxiosError(err);
  }
);

/**
 * Unwrap nested data response from backend.
 * After the axios response interceptor:
 * - Envelope with meta → `{ __zephixInner, __zephixMeta }`
 * - Envelope without meta → inner payload (often `{ favorites }`, etc.)
 * - Legacy → `{ data: T }` or `T` directly
 */
export function unwrapApiData<T>(response: unknown): T {
  if (response && typeof response === "object" && "__zephixInner" in response) {
    return (response as { __zephixInner: T }).__zephixInner;
  }
  if (response && typeof response === "object" && "data" in response) {
    return (response as { data: T }).data;
  }
  return response as T;
}

/**
 * Prefer reading `response.data` from a real Axios response (has numeric `status`).
 * POST /auth/login uses `res.json(loginResult)` — no `{ data, meta }` envelope — and
 * our axios response interceptor may forward the body in ways that still resolve to
 * AxiosResponse OR to the bare JSON object. If we always did `res.data` on a bare
 * `{ accessToken, refreshToken, user }`, `.data` is undefined and login loses tokens →
 * `/auth/me` has no Bearer → "Login succeeded but session not established".
 */
function isAxiosResponseShape(res: unknown): res is { data: unknown } {
  return (
    typeof res === "object" &&
    res !== null &&
    "data" in res &&
    "status" in res &&
    typeof (res as { status: unknown }).status === "number"
  );
}

function unwrapAxiosResponseData<T>(res: unknown): T {
  if (isAxiosResponseShape(res)) {
    const d = res.data;
    const inner = unwrapZephixClientPayload<T>(d);
    return (inner ?? d) as T;
  }
  const inner = unwrapZephixClientPayload<T>(res);
  return (inner ?? res) as T;
}

/**
 * Typed request wrapper that returns the unwrapped payload directly.
 * Always unwraps `response.data` (including `__zephixInner` when `meta` was present).
 *
 * Usage:
 *   const user = await request.get<User>('/users/me');
 *
 * IMPORTANT: Prefer this over raw `api` for JSON APIs. The raw `api` export
 * is for legacy code that manually reads `response.data`.
 */
export const request = {
  get: <T = unknown>(url: string, config?: Parameters<typeof api.get>[1]): Promise<T> =>
    api.get(url, config).then((res) => unwrapAxiosResponseData<T>(res)),
  post: <T = unknown>(url: string, data?: unknown, config?: Parameters<typeof api.post>[2]): Promise<T> =>
    api.post(url, data, config).then((res) => unwrapAxiosResponseData<T>(res)),
  put: <T = unknown>(url: string, data?: unknown, config?: Parameters<typeof api.put>[2]): Promise<T> =>
    api.put(url, data, config).then((res) => unwrapAxiosResponseData<T>(res)),
  patch: <T = unknown>(url: string, data?: unknown, config?: Parameters<typeof api.patch>[2]): Promise<T> =>
    api.patch(url, data, config).then((res) => unwrapAxiosResponseData<T>(res)),
  delete: <T = unknown>(url: string, config?: Parameters<typeof api.delete>[1]): Promise<T> =>
    api.delete(url, config).then((res) => unwrapAxiosResponseData<T>(res)),
};

/** @deprecated Use `request` instead. This alias exists for migration. */
export const typedApi = request;

// Default export for backward compatibility
export default api;
